import { v4 as uuidv4 } from 'uuid';

import { DropdownListItem, type IconType } from '@verantos-dev/shared-components';
import {
  DerivedEventTypeLegacy,
  FilterTypeLegacy,
  InputFieldTypeLegacy,
  type InputOptionContextDataLegacy,
  type InputFieldConfig,
  type InputFieldLegacy,
  SimpleEventTypeLegacy,
} from '@verantos-dev/types-cohorts';
import { ConceptGroup } from '@verantos-dev/types-concepts';

import { getFilteredConceptGroups } from './getFilteredConceptGroups';
import {
  COMPONENT_DROPDOWN_X_WIDE,
  COMPONENT_NUMERIC_INTEGER,
  COMPONENT_DROPDOWN_NARROW,
  COMPONENT_DROPDOWN_WIDE,
  COMPONENT_COMBOBOX_WIDE,
  COMPONENT_DROPDOWN_MEDIUM,
  COMPONENT_COMBOBOX_NARROW,
  COMPONENT_DEFAULT,
} from '../constants/constants';
import { tokenOptionsForTokenMap, tokenTypeForTokenMap } from '../constants/runtimeMaps';

type NewFilterForType = {
  newInputField: InputFieldLegacy;
  newInputFieldConfig: InputFieldConfig;
};

export const getNewFilterForType = (
  inputFieldIndex: number,
  filterType: FilterTypeLegacy,
  options: string[],
  inputOptionContextData: InputOptionContextDataLegacy
): NewFilterForType => {
  const id = uuidv4();

  /** set new input field with empty value */
  const newInputField: InputFieldLegacy = {
    id,
    value: '',
  };

  /** set new input field config */
  return {
    newInputField,
    newInputFieldConfig: {
      inputType: getInputTypeForFilterType(options),
      options: getInputOptions(filterType, options, inputOptionContextData),
      label: getLabelForFilterType(filterType, inputFieldIndex),
      placeholder: getPlaceholderForFilterType(
        filterType,
        inputFieldIndex,
        getInputTypeForFilterType(options)
      ),
      width: getWidthForFilterType(inputFieldIndex, filterType, inputOptionContextData),
    },
  };
};

/** helper functions for exported function above */
const getInputTypeForFilterType = (options: string[]): InputFieldTypeLegacy => {
  const inputTypes = options.map((option) => {
    return tokenTypeForTokenMap[option];
  });
  if (inputTypes.includes(InputFieldTypeLegacy.CONCEPT_COMBO_BOX)) {
    return InputFieldTypeLegacy.CONCEPT_COMBO_BOX;
  }

  if (inputTypes.includes(InputFieldTypeLegacy.COMBO_BOX)) {
    return InputFieldTypeLegacy.COMBO_BOX;
  }

  // THIS MUST BE HIGHER PRECEDENCE THAN INTEGERS FIELD TYPES
  if (inputTypes.includes(InputFieldTypeLegacy.FLOAT_NUMERIC_FIELD)) {
    return InputFieldTypeLegacy.FLOAT_NUMERIC_FIELD;
  }

  // PLEAS KEEP THIS BELOW THE FLOAT TYPE
  if (inputTypes.includes(InputFieldTypeLegacy.INT_NUMERIC_FIELD)) {
    return InputFieldTypeLegacy.INT_NUMERIC_FIELD;
  }

  if (inputTypes.includes(InputFieldTypeLegacy.TEXT_FIELD)) {
    return InputFieldTypeLegacy.TEXT_FIELD;
  }

  if (inputTypes.includes(InputFieldTypeLegacy.DROPDOWN)) {
    return InputFieldTypeLegacy.DROPDOWN;
  }
  return InputFieldTypeLegacy.DROPDOWN;
};

export const transformConceptGroupIdToOptionsValue = (conceptGroupId: string): string => {
  return `CONCEPT_GROUP::"${conceptGroupId}"`;
};

export const transformConceptGroupOptionsValueToId = (conceptGroupOptionsValue: string): string => {
  return conceptGroupOptionsValue.replace('CONCEPT_GROUP::"', '').replace('"', '');
};

const getInputOptions = (
  filterType: FilterTypeLegacy,
  options: string[],
  inputOptionContextData: InputOptionContextDataLegacy
): DropdownListItem[] => {
  const {
    eventNames,
    conceptGroups,
    currentCohortDefinition,
    currentFilter,
    featureFlags,
    activeCohortBuilderItem,
  } = inputOptionContextData;

  const nextOptions = options.reduce((accu: DropdownListItem[], optionToken) => {
    // We get the options for the token from the tokenOptionsForTokenMap
    const optionsForToken: DropdownListItem[] = tokenOptionsForTokenMap[optionToken]
      // We manage a list of options that are supported by the UI for each token
      // that is parsed from the grammar as a potential next valid token, tokenOptionsForTokenMap
      ?.filter(({ featureFlag }) => {
        // If the feature flag is set and the feature flag value is false, we don't want to show the option
        // In all other cases, we want to show the option
        return !(featureFlag && featureFlags?.[featureFlag] === false);
      })
      ?.map(({ displayValue, grammarValue }) => ({
        value: grammarValue,
        displayValue,
      }))

      // In some cases the grammar is more expressive than what we want to permit in the UI at this point
      // For example, we don't want to allow the user to select the '=' or '!=' tokens for a concept
      // attribute, so we filter those out here
      .filter((option) => {
        // We don't want to allow the user to select the '=' or '!=' tokens for a concept attribute comparison
        if ([FilterTypeLegacy.CONCEPT, FilterTypeLegacy.RELATED_ENTITY].includes(filterType)) {
          return !['=', '!='].includes(option.value);
        }

        // we don't want to allow the user to select 'Age at index' if the index date is not set
        if (!currentCohortDefinition?.indexDate && filterType === FilterTypeLegacy.NUMERIC) {
          return option.value !== 'AGE_AT_INDEX';
        }

        return true;
      });

    return [...accu, ...(optionsForToken ?? [])];
  }, []);

  /** Option Transformations
   * There are some special cases where we want to transform the options that are available to the user
   * or inject external data into the options that are available to the user.
   *
   * For example, we want to inject the concept group names into the options for the concept group token
   * so that the user can select a concept group from a dropdown.
   */

  // transform input options to concept group names
  if (nextOptions.some((option) => option.value === 'CONCEPT_GROUP')) {
    // TODO: The complete logic of filters and input fields needs to be refactored and simplified
    // For related entity, we have 4 input fields and the attribute is the second one
    const attributeIndex = currentFilter?.inputFields?.length === 4 ? 1 : 0;
    const filterValue = currentFilter?.inputFields?.[attributeIndex]?.value ?? '';

    const eventType = activeCohortBuilderItem?.type ?? '';

    const filteredConceptGroups = getFilteredConceptGroups(
      conceptGroups as ConceptGroup[],
      filterValue,
      eventType as SimpleEventTypeLegacy
    );

    return (
      filteredConceptGroups?.map((conceptGroup) => {
        const isVerantosCreated = conceptGroup.owner?.toLowerCase?.() === 'verantos';
        const icon: IconType = isVerantosCreated ? 'verantosLogo' : 'owner';
        return {
          displayValue:
            conceptGroup?.conceptGroupName.charAt?.(0).toUpperCase?.() +
            conceptGroup?.conceptGroupName.slice?.(1),
          value: transformConceptGroupIdToOptionsValue(conceptGroup.conceptGroupId),
          icon,
          actionButtonIcon: 'fullscreen',
        };
      }) ?? []
    );
  }

  // inject event names for each simple and derived events
  if (
    nextOptions.some((option) =>
      [
        'CONDITION_OCCURRENCE_ALIAS',
        'DRUG_EXPOSURE_ALIAS',
        'MEASUREMENT_ALIAS',
        'OBSERVATION_ALIAS',
        'PROCEDURE_OCCURRENCE_ALIAS',
        'VISIT_OCCURRENCE_ALIAS',
        'MERGED_EVENT_NAME',
        'SEQUENCE_EVENT_NAME',
        'INSTANCE_EVENT_NAME',
      ].includes(option.value ?? '')
    )
  ) {
    return eventNames ?? [];
  }

  return nextOptions;
};

const getLabelForFilterType = (filterType: FilterTypeLegacy, inputFieldIndex: number): string => {
  switch (filterType) {
    case FilterTypeLegacy.CONCEPT:
      if (inputFieldIndex === 0) {
        return 'Concept attribute';
      }
      if (inputFieldIndex === 1) {
        return 'Comparator';
      }
      if (inputFieldIndex === 2) {
        return 'Concept group';
      }

      return 'Attribute';
    case FilterTypeLegacy.NUMERIC:
      if (inputFieldIndex === 0) {
        return 'Attribute';
      }
      if (inputFieldIndex === 1) {
        return 'Comparator';
      }
      if (inputFieldIndex === 2) {
        return 'Number';
      }
      return 'Numeric attribute';
    case FilterTypeLegacy.RELATED_ENTITY:
      if (inputFieldIndex === 0) {
        return 'Entity';
      }
      if (inputFieldIndex === 1) {
        return 'Attribute';
      }
      if (inputFieldIndex === 2) {
        return 'Comparator';
      }
      if (inputFieldIndex === 3) {
        return 'Concept group';
      }

      return 'Attribute';
    case FilterTypeLegacy.UNIQUENESS:
      if (inputFieldIndex === 0) {
        return 'Filter type';
      }
      if (inputFieldIndex === 1) {
        return 'Time value';
      }
      if (inputFieldIndex === 2) {
        return 'Time unit';
      }
      if (inputFieldIndex === 3) {
        return 'Date attribute';
      }

      return 'Attribute';

    case FilterTypeLegacy.TEMPORAL:
      if (inputFieldIndex === 0) {
        return 'Constraint type';
      }
      if (inputFieldIndex === 1) {
        return 'Time value';
      }
      if (inputFieldIndex === 2) {
        return 'Time unit';
      }
      return 'Attribute';

    case FilterTypeLegacy.COUNT:
      if (inputFieldIndex === 0) {
        return 'Event';
      }
      if (inputFieldIndex === 1) {
        return 'Comparator';
      }
      if (inputFieldIndex === 2) {
        return 'Count';
      }
      return 'Attribute';

    default:
      return 'Attribute';
  }
};

const getPlaceholderForFilterType = (
  filterType: FilterTypeLegacy,
  inputFieldIndex: number,
  InputType: InputFieldTypeLegacy
): string => {
  switch (filterType) {
    case FilterTypeLegacy.CONCEPT:
      return 'Select concept attribute';
    case FilterTypeLegacy.NUMERIC:
      if (inputFieldIndex === 0) {
        return 'Select attribute';
      }
      if (inputFieldIndex === 1) {
        return '';
      }
      if (inputFieldIndex === 2) {
        return InputType === InputFieldTypeLegacy.FLOAT_NUMERIC_FIELD ? '0.000' : '0';
      }
      return '';
    case FilterTypeLegacy.RELATED_ENTITY:
      if (inputFieldIndex === 0) {
        return 'Select entity';
      }
      if (inputFieldIndex === 1) {
        return 'Select attribute';
      }
      if (inputFieldIndex === 2) {
        return '';
      }
      return 'Select group';
    case FilterTypeLegacy.UNIQUENESS:
      if (inputFieldIndex === 0) {
        return 'Select type';
      }
      if (inputFieldIndex === 1) {
        return '0';
      }
      if (inputFieldIndex === 2) {
        return 'Unit';
      }
      return '';

    case FilterTypeLegacy.TEMPORAL: {
      if (inputFieldIndex === 0) {
        return 'Select constraint type';
      }
      if (inputFieldIndex === 1) {
        return '0';
      }
      if (inputFieldIndex === 2) {
        return '';
      }
      return '';
    }
    case FilterTypeLegacy.COUNT: {
      if (inputFieldIndex === 0) {
        return 'Select event';
      }
      if (inputFieldIndex === 1) {
        return '';
      }
      if (inputFieldIndex === 2) {
        return '0';
      }
      return 'Select attribute';
    }
    default:
      return '';
  }
};

const getWidthForFilterType = (
  inputFieldIndex: number,
  filterType: FilterTypeLegacy,
  { derivedEventType }: InputOptionContextDataLegacy
): number => {
  if (derivedEventType === DerivedEventTypeLegacy.SEQUENCE) {
    if (filterType === FilterTypeLegacy.TEMPORAL) {
      if (inputFieldIndex === 0) {
        return COMPONENT_DROPDOWN_X_WIDE;
      }
      if (inputFieldIndex === 1) {
        return COMPONENT_NUMERIC_INTEGER;
      }
      if (inputFieldIndex === 2) {
        return COMPONENT_DROPDOWN_NARROW;
      }
    }
  }

  if (filterType === FilterTypeLegacy.CONCEPT) {
    if (inputFieldIndex === 0) {
      return COMPONENT_DROPDOWN_WIDE;
    }
    if (inputFieldIndex === 1) {
      return COMPONENT_DROPDOWN_NARROW;
    }
    if (inputFieldIndex === 2) {
      return COMPONENT_COMBOBOX_WIDE;
    }
  }

  // Subject variable count widths
  if (filterType === FilterTypeLegacy.COUNT) {
    if (inputFieldIndex === 0) {
      return COMPONENT_DROPDOWN_X_WIDE;
    }
    if (inputFieldIndex === 1) {
      return COMPONENT_DROPDOWN_NARROW;
    }
    if (inputFieldIndex === 2) {
      return COMPONENT_NUMERIC_INTEGER;
    }
  }

  if (filterType === FilterTypeLegacy.NUMERIC) {
    if (inputFieldIndex === 0) {
      return COMPONENT_DROPDOWN_WIDE;
    }
    if (inputFieldIndex === 1) {
      return COMPONENT_DROPDOWN_NARROW;
    }
    if (inputFieldIndex === 2) {
      return COMPONENT_NUMERIC_INTEGER;
    }
  }

  if (filterType === FilterTypeLegacy.RELATED_ENTITY) {
    if (inputFieldIndex === 0 || inputFieldIndex === 1) {
      return COMPONENT_DROPDOWN_MEDIUM;
    }
    if (inputFieldIndex === 2) {
      return COMPONENT_DROPDOWN_NARROW;
    }
    if (inputFieldIndex === 3) {
      return COMPONENT_COMBOBOX_NARROW;
    }
  }

  if (filterType === FilterTypeLegacy.UNIQUENESS) {
    if (inputFieldIndex === 0) {
      return COMPONENT_COMBOBOX_NARROW;
    }
    if (inputFieldIndex === 1) {
      return COMPONENT_NUMERIC_INTEGER;
    }
    if (inputFieldIndex === 2) {
      return COMPONENT_DROPDOWN_NARROW;
    }
    if (inputFieldIndex === 3) {
      return COMPONENT_DROPDOWN_WIDE;
    }
  }

  return COMPONENT_DEFAULT;
};
